import { ContactCommunicationType, DocumentType } from './generated/graphql';

export const ACTIVE_CONTACT_COMMUNICATION_TYPES: ContactCommunicationType[] = [
  ContactCommunicationType.PerAppointmentDateChanged,
  ContactCommunicationType.PerOrderDriverOnSite,
  ContactCommunicationType.PerOrderCompleted,
  ContactCommunicationType.PerOrderOutForDelivery,
];

export const ONE_SECOND_IN_MS = 1000;
export const ONE_MINUTE_IN_MS = 60 * ONE_SECOND_IN_MS;

/**
 * Single source of truth for the dunning custom-message cap, shared by the
 * backend DTO validators and the frontend modal field so the two cannot drift.
 */
export const DUNNING_CUSTOM_MESSAGE_MAX_LENGTH = 5000;

/**
 * Upper bound on ONE side of ONE dimension of a scheduling resource's
 * eligibility rules. Single source of truth for the backend DTO's
 * `@ArrayMaxSize` decorators and the frontend form that checks a list before
 * spending a round trip on it.
 *
 * An abuse bound rather than a business limit: every list reaches an `IN`
 * clause and a `createMany`, so an unbounded one is the expensive shape. A
 * tenant that legitimately needs more than this many customers on an allow
 * list is a product conversation, not a number to quietly raise.
 *
 * Shared rather than mirrored because the input it guards is NOT
 * `@SkipValidation`: crossing the bound on the server is a raw GraphQL
 * validation error rather than a readable refusal, so the form has to know the
 * same number to catch it first.
 */
export const MAXIMUM_ELIGIBILITY_RULE_SUBJECTS = 500;

/**
 * Upper bound on how many partners one partner settings save may change.
 * Single source of truth for the backend DTO's `@ArrayMaxSize` and the
 * frontend draft store that stops staging a further partner at this count.
 *
 * Shared rather than mirrored because the draft spans every page of the
 * partners list: crossing the bound on the server is a raw GraphQL validation
 * error rather than a readable refusal, so the draft has to know the same
 * number to catch it first.
 */
export const MAXIMUM_ORGANIZATION_PEER_SETTINGS_CHANGES = 100;

/**
 * Upper bound on how many checked orders an orders-table "Export selected
 * orders" CSV export may name. Single source of truth for the backend DTO's
 * `@ArrayMaxSize` on `CreateOrderTableReportInput.selectedOrderUuids` and the
 * frontend menu that disables the item before spending a round trip on it.
 *
 * Applies only to the checked-row list. The inherited `uuids` field on the same
 * input is view scoping (the Audit Report tabs send every order in a bucket
 * through it) and is intentionally uncapped.
 *
 * Matches the backend's general `MAX_UUID_LIST_SIZE` for uuid lists that feed
 * an `IN` clause. The grid's largest page is 100 rows, so only a select-all
 * across pages can exceed this, and "Export all orders in this view" (which
 * sends no selected-order list, only the view's own filters and scoping) is
 * the right tool for that case.
 */
export const MAX_ORDER_TABLE_REPORT_SELECTED_ORDERS = 500;

/**
 * The number of decimal places to use for numerical scalars.
 */
export const DECIMAL_PRECISION = 10;

export const HOURS_PER_DAY = 24;
export const DAYS_PER_YEAR = 365;
export const HOURS_PER_YEAR = HOURS_PER_DAY * DAYS_PER_YEAR;

/**
 * Upper bound on how many times a customer may move their own appointment.
 *
 * Ten rather than a larger round number because each move is not free: every
 * one sends the customer an SMS, pushes an outbound EDI 214 at a trading
 * partner and does notification-engine work. This is an abuse bound on that
 * fan-out, not a business opinion about how many changes are reasonable; a
 * tenant that wants more is a product conversation.
 */
const MAXIMUM_SELF_RESCHEDULE_LIMIT = 10;

/**
 * Inclusive bounds for the five `CompanyConfiguration` columns that govern
 * appointment booking. Single source of truth for the backend DTO validators
 * and the Settings, Dispatch form, so the form cannot end up refusing a value
 * the API accepts, or accepting one the API refuses.
 *
 * The server stays the authority; the form repeats these only so an operator
 * finds out before the round trip rather than after it.
 *
 * Membership of this object is load-bearing beyond validation:
 * `CompanyConfigurationsService` derives the field list it gates behind
 * `SETTINGS_DISPATCH` (WRITE) from these keys, so a column added here is
 * permission-gated the day it is added rather than the day somebody notices.
 *
 * Why each bound is where it is:
 * - a slot may not exceed a day, because the booking code offers slots within a
 *   single date, and may not be zero, because the slot query derives its
 *   minimum availability overlap as this value minus one
 * - a notice or a horizon of zero is a meaningful setting and so is allowed
 * - a year caps every notice and the horizon, past which the cutoff walk gives
 *   up looking for an operating day to land on
 * - zero self-reschedules is a meaningful setting -- book once, then talk to a
 *   human -- and the upper bound exists to cap the outbound fan-out a move
 *   costs
 */
export const APPOINTMENT_SCHEDULING_BOUNDS = {
  appointmentSchedulingSlotHours: { minimum: 1, maximum: HOURS_PER_DAY },
  appointmentSchedulingLeadingBufferHours: {
    minimum: 0,
    maximum: HOURS_PER_YEAR,
  },
  appointmentSchedulingCutoffDays: { minimum: 0, maximum: DAYS_PER_YEAR },
  appointmentChangeCutoffHours: { minimum: 0, maximum: HOURS_PER_YEAR },
  appointmentSelfRescheduleLimit: {
    minimum: 0,
    maximum: MAXIMUM_SELF_RESCHEDULE_LIMIT,
  },
} as const;

/**
 * The time zones the general settings control offers. The API accepts any
 * IANA name (`@IsIanaTimeZone()`), so this is a curated shortlist, not a
 * validation boundary.
 */
export const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Denver',
  'America/Phoenix',
  'America/Chicago',
  'America/Los_Angeles',
] as const;

/**
 * Upper bound on `lineHaulBuildAheadDays`. Mirrors the per-lane override's own
 * ceiling in `CreateRecurringLineHaulManifestTemplateInput`: every generation
 * tick enumerates (truck, day) slots across the horizon for every active lane,
 * and the horizon is handed to a run generator capped at 90 days.
 */
export const LINE_HAUL_BUILD_AHEAD_DAYS_MAX = 30;

export const QUOTE_AGENT_SETTING_BOUNDS = {
  quoteAgentAutoSendMaxCents: { minimum: 0, maximum: 10_000_000 },
  quoteAgentMinConfidence: { minimum: 0, maximum: 100 },
  quoteAgentValidityHours: { minimum: 1, maximum: 720 },
  quoteAgentDailyModelBudgetCents: { minimum: 0, maximum: 100_000 },
} as const;

// These should be alphabetically sorted, except for "Other" which should be last
export const DOCUMENT_TYPES_FOR_SELECTION: DocumentType[] = [
  DocumentType.DeliveryAlert,
  DocumentType.DeliveryReceipt,
  DocumentType.DigitalProofOfDelivery,
  DocumentType.EndOfDayDocument,
  DocumentType.PickupAlert,
  DocumentType.ProofOfDelivery,
  DocumentType.ProofOfDeliveryScanned,
  DocumentType.ShipmentPhoto,
  DocumentType.Other,
  DocumentType.Hawb,
  DocumentType.Mawb,
  DocumentType.CommercialInvoice,
  DocumentType.Manifest,
  DocumentType.BackupEmail,
  DocumentType.CheckCopy,
  DocumentType.TransferManifest,
  DocumentType.OvernightSlip,
  DocumentType.ArrivalNotice,
  DocumentType.Hazmat,
  DocumentType.VoidedCheck,
  DocumentType.DamageReport,
  DocumentType.Rejection,
  DocumentType.AbiNotes,
  DocumentType.ItemizedTransportDocument,
  DocumentType.TransportApprovalCertificate,
  DocumentType.CustomsRelease,
  DocumentType.Ace,
  DocumentType.PrepaidReceipt,
].sort((a, b) => a.localeCompare(b));

// These should be alphabetically sorted, except for "Other" which should be last
export const DOCUMENT_TYPES_FOR_SELECTION_WITH_ESIGN_FLOW: DocumentType[] = [
  DocumentType.DeliveryAlert,
  DocumentType.DeliveryReceipt,
  DocumentType.DigitalProofOfDelivery,
  DocumentType.DigitalProofOfPickup,
  DocumentType.EndOfDayDocument,
  DocumentType.PickupAlert,
  DocumentType.PickupReceiptForEsign,
  DocumentType.ProofOfDelivery,
  DocumentType.ProofOfDeliveryScanned,
  DocumentType.ShipmentPhoto,
  DocumentType.Other,
  DocumentType.Hawb,
  DocumentType.Mawb,
  DocumentType.CommercialInvoice,
  DocumentType.Manifest,
  DocumentType.BackupEmail,
  DocumentType.CheckCopy,
  DocumentType.TransferManifest,
  DocumentType.OvernightSlip,
  DocumentType.ArrivalNotice,
  DocumentType.Hazmat,
  DocumentType.VoidedCheck,
  DocumentType.DamageReport,
  DocumentType.Rejection,
  DocumentType.AbiNotes,
  DocumentType.ItemizedTransportDocument,
  DocumentType.TransportApprovalCertificate,
  DocumentType.CustomsRelease,
  DocumentType.Ace,
  DocumentType.PrepaidReceipt,
].sort((a, b) => a.localeCompare(b));

/**
 * Version of the SMS consent wording. ONE VERSION COVERS TWO TEXTS: the
 * disclosure beside the checkbox on the public opt-in page, and the
 * confirmation message the backend then attempts to send. The backend records
 * it on the consent history, which is what shows later exactly what a person
 * agreed to.
 *
 * Shared rather than mirrored because the page posts the version it rendered,
 * and the backend refuses a submission whose version is not this one. A page
 * left open across a wording change therefore records nothing until reloaded.
 *
 * BUMP IT ON ANY CHANGE TO EITHER TEXT, however small, and record both texts
 * under the new version: the message in the backend's
 * `sms-opt-in.service.spec.ts`, the disclosure in the frontend's
 * `sms-opt-in-view.spec.tsx`. Each spec fails until its text is recorded.
 */
export const SMS_CONSENT_WORDING_VERSION = '2026-10-v2';
